import { storesAPI } from '@api/main';
import { getStoredCountry, getStoredCurrency, setStoredCountry, setStoredCurrency } from '@utils/cookieUtils';

export interface LocaleData {
  country: string;
  currency: string;
  symbol: string;
}

class LocaleService {
  private isInitialized = false;
  private initializationPromise: Promise<LocaleData> | null = null;

  /**
   * Initialize locale data by checking cookies first, then calling API if needed
   * This ensures the app doesn't start without country and currency values
   */
  async initialize(): Promise<LocaleData> {
    if (this.isInitialized) {
      return this.getCurrentLocale();
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.performInitialization();
    return this.initializationPromise;
  }

  private async performInitialization(): Promise<LocaleData> {
    try {
      // Check if cookies are already set
      const storedCountry = getStoredCountry();
      const storedCurrency = getStoredCurrency();

      if (storedCountry && storedCurrency) {
        // Cookies are already set, use them
        this.isInitialized = true;
        return {
          country: storedCountry,
          currency: storedCurrency,
          symbol: this.getCurrencySymbol(storedCurrency)
        };
      }

      // Cookies are not set, call API to get default locale
      console.log('Locale cookies not found, fetching default locale from API...');
      const defaultLocale = await storesAPI.getDefaultLocale();
      
      // Set cookies with default values
      setStoredCountry(defaultLocale.country);
      setStoredCurrency(defaultLocale.currency);
      
      this.isInitialized = true;
      console.log('Locale initialized with default values:', defaultLocale);
      
      return {
        country: defaultLocale.country,
        currency: defaultLocale.currency,
        symbol: defaultLocale.symbol
      };
    } catch (error) {
      console.error('Failed to initialize locale:', error);
      
      // Fallback to default values if API fails
      const fallbackCountry = 'India';
      const fallbackCurrency = 'INR';
      
      setStoredCountry(fallbackCountry);
      setStoredCurrency(fallbackCurrency);
      
      this.isInitialized = true;
      console.log('Locale initialized with fallback values:', { country: fallbackCountry, currency: fallbackCurrency });
      
      return {
        country: fallbackCountry,
        currency: fallbackCurrency,
        symbol: this.getCurrencySymbol(fallbackCurrency)
      };
    }
  }

  /**
   * Get current locale data from cookies
   */
  getCurrentLocale(): LocaleData {
    const country = getStoredCountry();
    const currency = getStoredCurrency();
    
    if (!country || !currency) {
      // Fallback to default values if cookies are not found
      const fallbackCountry = 'India';
      const fallbackCurrency = 'INR';
      
      console.warn('Locale cookies not found, using fallback values:', { country: fallbackCountry, currency: fallbackCurrency });
      
      return {
        country: fallbackCountry,
        currency: fallbackCurrency,
        symbol: this.getCurrencySymbol(fallbackCurrency)
      };
    }
    
    return {
      country,
      currency,
      symbol: this.getCurrencySymbol(currency)
    };
  }

  /**
   * Update locale data and cookies
   */
  updateLocale(country: string, currency: string): void {
    setStoredCountry(country);
    setStoredCurrency(currency);
  }

  /**
   * Check if locale is initialized
   */
  isLocaleInitialized(): boolean {
    return this.isInitialized && !!getStoredCountry() && !!getStoredCurrency();
  }

  /**
   * Get currency symbol for display
   */
  private getCurrencySymbol(currency: string): string {
    const symbols: { [key: string]: string } = {
      'INR': '₹',
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'CAD': 'CA$',
      'BRL': 'R$',
      'MYR': 'RM',
      'MOP': 'MOP$',
      'JOD': 'JOD',
      'AED': 'AED',
      'SAR': 'SAR',
      'AUD': 'A$'
    };
    return symbols[currency] || currency;
  }
}

export const localeService = new LocaleService(); 